import {
    GridControlTypes,
    ColumnTypes,
    DisplayModes,
    EditModes,
    CssClass,
    NotificationTypes,
    ColumnFilterTypes,
    GridTypes,
    AggregateFunctions,
    EncodingTypes,
    TextBlockCategory,
} from "Enums";
import { IControl } from "Controls/Interfaces";
import { IGrid, IGridDefinition, IControlDefinition, IGridColumnDefinition, IUserGridConfigDefinition } from "Grids/Interfaces";
import { BaseGrid } from "./BaseGrid";
import { GridSettings } from "Configuration";
import { CONTROL_ID_ATTRIBUTE_NAME, CONTROL_VISIBILITY_PROPERTY_NAME } from "Controls/Control";
import { CommandService, TextBlockService, ExportService, ComboboxService, GridDefinitionService } from "Service";
import { CoreContext, Util, EditContext } from "Core";
import { EventArgs } from "Core/Events";
import { IDataRow, IODataResult, ITextBlockArray, IExportColumn } from "Entities";
import { IDisposable } from "Core/Interfaces";
import { ControlEventTypes, GridEventTypes } from "Core/Events/EventTypes";
import { DataConstants } from "Constants";
import { NumberControl } from "Controls";
import { IGridUserConfig } from "Grids/Interfaces/IGridUserConfig";

/**
 * ## Grid
 *
 * Showing data as table view.
 * @legacy Formerly known as **AdvancedGrid**.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Grid with the name GridName
 * // In script:
 * @@grd:GridName@@
 *
 * // On page:
 * $dms("GridName")
 *
 * // First Grid on page:
 * $dms(".dms-grid")
 * ```
 * @module Grids
 */
export class Grid extends BaseGrid implements IGrid, IDisposable {
    /**
     * @hidden
     */
    protected _kendoElement: kendo.ui.Grid;

    /**
     * @hidden
     */
    protected gridDefinition: IGridDefinition;

    private currentEditUid: string;

    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/Grid.html");

    private insertRowModel: any;

    private sortRequiredFields: { [field: string]: boolean } = {};

    private toolbarEditContext: EditContext;

    /**
     * @hidden
     */
    protected controlDefinitionDict: { [index: number]: IControlDefinition } = {};
    private columnDefinitionsByName: { [columnName: string]: IGridColumnDefinition } = {};

    // contains the first value column name for each columnname (= field of GridColumn).
    private firstTextColumnForColumnName: { [columnName: string]: string } = {};

    // contains the first value column name for each columnname (= field of GridColumn).
    private firstFilterColumnForColumnName: { [columnName: string]: string } = {};

    // contains the first aggregate column name for each columnname (= field of GridColumn).
    private firstAggregateColumnForColumnName: { [columnName: string]: string } = {};

    private toolbarControls: { [controlId: number]: IControl } = {};

    /**
     * @hidden
     */
    protected commandOption: any; // last command option for read command. used in export.

    /**
     * @hidden
     */
    protected _saveScrollPos = false;

    private exportService: ExportService;

    /**
     * @hidden
     */
    protected columnSelectorColumnNames: string[] = [];

    /**
     * Create a new Grid.
     * @param gridDefinition GridDefinition.
     * @param gridSettings GridSettings.
     * @hidden
     */
    constructor(
        gridDefinition: IGridDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        comboboxService: ComboboxService,
        commandService: CommandService,
        exportService: ExportService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService
    ) {
        super(gridDefinition, textBlockCategory, gridSettings, comboboxService, commandService, gridDefinitionService, textBlockService);
        this.gridDefinition = gridDefinition;
        this.isAutoResize = Util.stringIsNullOrEmpty(gridDefinition.GridHeight);
        this.exportService = exportService;
    }

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    protected async setElementInternal(element: JQuery): Promise<void> {
        await this.textBlockService.getAll();
        let gridOptions = await this.getGridOptions();
        this._kendoElement = new kendo.ui.Grid(this.jQueryElement[0], gridOptions);

        if (this.gridDefinition.ShowRefreshButton) {
            // reset the refresh button to invoke the correct method.
            this.jQueryElement.on("click", ".k-pager-refresh", (e) => {
                e.preventDefault();
                this.reload();
            });
        }

        this.jQueryElement.on("click", "." + CssClass.addToolbar, (e) => this.onAddButton());
        this.jQueryElement.on("click", "." + CssClass.export, (e) => this.onExportButton());

        // attach the double click handler on normal table rows which are not editable
        this.jQueryElement.on("dblclick", "tr[data-uid]:not(.k-grid-edit-row)", (e) => this.onRowDoubleClick(e));

        this.initializeToolbar();

        if (this.gridDefinition.CanHideColumns) {
            let columnChooser = <any>$(`<div class="dms-column-chooser k-button k-button-icon"></div>`);
            this.jQueryElement.prepend(columnChooser);
            new (<any>kendo.ui).ColumnMenu(columnChooser[0], <kendo.ui.GridColumnMenu>{
                filterable: false,
                sortable: false,
                dataSource: this._kendoElement.dataSource,
                columns: true,
                messages: {
                    columns: this.textBlockService.get("GridColumnChoose", TextBlockCategory.global),
                },
                owner: this._kendoElement,
            });
            columnChooser.find(".k-i-more-vertical").removeClass("k-i-more-vertical k-icon").addClass("fa fa-lg fa-cog");
        }

        this.jQueryElement.find(".dms-helptext").on("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.tooltipExtension.showHelpTextForElement($(e.target));
        });
    }

    /**
     * Occurs when a row is double clicked.
     * @param e Eventargs.
     */
    private onRowDoubleClick(e: JQuery.DoubleClickEvent) {
        const uid = $(e.currentTarget).data("uid");
        const defaultButtons = this._kendoElement.element.find(`tr[data-uid='${uid}'] .${CssClass.defaultButton}`);
        if (defaultButtons && defaultButtons.length > 0) {
            const defaultButton = defaultButtons[0];
            defaultButton.click();
        }
    }

    private resetInsertRowModel(): void {
        let row = (<any>this._kendoElement.dataSource)._createNewModel();
        row[DataConstants.ROW_INDEX_COLUMN_NAME] = -1;
        row.id = -1;
        this.insertRowModel = row;
    }

    private userConfig: IGridUserConfig;

    /**
     * Get options used to initialize the kendo grid.
     * @param textBlockCategory Name of the textblockCategory.
     * @hidden
     */
    protected async getGridOptions(): Promise<kendo.ui.GridOptions> {
        this.userConfig = undefined;
        if (this.currentUserGridConfig && this.currentUserGridConfig.Configuration) {
            try {
                this.userConfig = JSON.parse(this.currentUserGridConfig.Configuration);
            } catch (e) {
                console.error("error in user grid configuration: " + e + " " + this.currentUserGridConfig.Configuration);
            }
        }

        let definition = this.getColumnsAndFields(this.userConfig);

        let filterTbs: ITextBlockArray;
        if (definition.filterable) {
            let tbs = await this.textBlockService.load(CoreContext.Environment.LanguageCode, "filteroptions");
            filterTbs = tbs["filteroptions"];
        }

        let dataSourceOptions: kendo.data.DataSourceOptions = {
            transport: {
                read: (options: kendo.data.DataSourceTransportRead) => {
                    this.setBusy(true);
                    //setTimeout(() => this.setBusy(true), 1);
                    if (options.data.sort) {
                        for (let sortIdx = 0, sortLen = options.data.sort.length; sortIdx < sortLen; sortIdx++) {
                            let sortDefinition = options.data.sort[sortIdx];
                            let columnName = <string>sortDefinition.field;
                            let fieldName = this.firstTextColumnForColumnName[columnName];
                            if (fieldName) {
                                sortDefinition.field = fieldName;
                            }
                        }
                    }
                    if (options.data.aggregate) {
                        for (let aggIdx = 0, aggLen = options.data.aggregate.length; aggIdx < aggLen; aggIdx++) {
                            let aggDefinition = options.data.aggregate[aggIdx];
                            let aggColumnName = <string>aggDefinition.field;
                            let aggFieldName = this.firstAggregateColumnForColumnName[aggColumnName];
                            if (aggFieldName) {
                                aggDefinition.field = aggFieldName;
                                aggDefinition.columnName = aggColumnName;
                            }
                        }
                    }

                    if (options.data.group) {
                        for (let groupIdx = 0, groupLen = options.data.group.length; groupIdx < groupLen; groupIdx++) {
                            let groupDefinition = options.data.group[groupIdx];
                            let columnName = <string>groupDefinition.field;
                            let fieldName = this.firstTextColumnForColumnName[columnName];
                            if (fieldName) {
                                groupDefinition.field = fieldName;
                                groupDefinition.columnName = columnName;
                            }

                            if (groupDefinition.aggregates) {
                                for (let aggIdx = 0, aggLen = groupDefinition.aggregates.length; aggIdx < aggLen; aggIdx++) {
                                    let aggDefinition = groupDefinition.aggregates[aggIdx];
                                    let aggColumnName = <string>aggDefinition.field;
                                    let aggFieldName = this.firstAggregateColumnForColumnName[aggColumnName];
                                    if (aggFieldName) {
                                        aggDefinition.field = aggFieldName;
                                        aggDefinition.columnName = aggColumnName;
                                    }
                                }
                            }
                        }
                    }

                    this.commandOption = options.data;
                    this.commandService.get(this.gridSettings.dataUrlGet, this.gridDefinition.GridID, options.data).then((result) => {
                        // if the add line is shown immediately, add a dummy row to the result set.
                        if (this.gridDefinition.AddAllowed && this.gridDefinition.ShowAddLineImmediately) {
                            if (this.insertRowModel == null) {
                                this.resetInsertRowModel();
                            }

                            // if data is grouped, add a dummy group for the first level, otherwise the grid fails to render.
                            // this dummy group header will be hidden in dataBound-event.
                            if (options.data.group.length > 0) {
                                let group = options.data.group[0];
                                let groupEntry: any = {};
                                groupEntry.aggregates = [];
                                groupEntry.hasSubgroups = false;
                                groupEntry.field = group.field;
                                groupEntry.value = null;
                                groupEntry.items = [this.insertRowModel];
                                result.value.unshift(groupEntry);
                            } else {
                                result.value.unshift(this.insertRowModel);
                            }
                        }
                        try {
                            (options.success as JQuery.Ajax.SuccessCallback<any>)(result, null, null);
                        } catch (e) {
                            console.log(e);
                        }
                    });
                },
                create: function (options: kendo.data.DataSourceTransportCreate) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
                update: function (options: kendo.data.DataSourceTransportUpdate) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
                destroy: function (options: kendo.data.DataSourceTransportDestroy) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
            },
            schema: {
                aggregates: function (data: IODataResult) {
                    return data.aggregateresults;
                },
                data: function (data: IODataResult) {
                    return data.value;
                },
                total: function (data: IODataResult) {
                    return data["odata.count"];
                },
                groups: function (data: IODataResult) {
                    return data.value;
                },
                model: {
                    fields: definition.fields,
                    id: DataConstants.ROW_INDEX_COLUMN_NAME,
                },
                errors: "error",
            },
            pageSize: (this.userConfig && this.userConfig.PageSize) || this.gridDefinition.PageSize || 50,
            // pageSize: 100,
            serverPaging: true,
            serverSorting: true,
            serverFiltering: true,
            serverGrouping: true,
            serverAggregates: true,
            batch: false,
            aggregate: definition.aggregateItems,
            sort: definition.sortingFields,
            group: definition.groupingFields,
            error: (e) => this.onError(e),
            autoSync: false,
        };

        if (this.gridDefinition.FormatDefinitions && this.gridDefinition.FormatDefinitions.length != 0) {
            let formatDefinitions = this.gridDefinition.FormatDefinitions;
            $.extend(dataSourceOptions.schema.model, {
                __dms_rowCssClass: function () {
                    return Util.getFormatCondition(this, formatDefinitions);
                },
            });
        }

        if (definition.modelExtensions) {
            $.extend(dataSourceOptions.schema.model, definition.modelExtensions);
        }

        let options: kendo.ui.GridOptions = {
            dataSource: new kendo.data.DataSource(dataSourceOptions),
            autoBind: false,
            toolbar: this.getToolbar(),
            editable: {
                confirmation: false, // disable confirmation messages for delete
                mode: "inline",
            },
            navigatable: false,
            sortable: {
                showIndexes: true,
                mode: this.gridDefinition.CanSortByMultipleColumns ? "multiple" : "single",
            },
            pageable: {
                refresh: this.gridDefinition.ShowRefreshButton || false,
                pageSizes: this.getPageSizes(),
                messages: {
                    empty: this.textBlockService.get("advancedGridNoRecordsText", this.textBlockCategory),
                    allPages: this.textBlockService.get("advancedGridPageSizeAllText", this.textBlockCategory),
                    itemsPerPage: this.textBlockService.get("advancedGridPageSizeLabelText", this.textBlockCategory),
                    display: this.textBlockService
                        .get("advancedGridPagerTextFormat", this.textBlockCategory)
                        .replace("@@itemsCount@@", "{2}")
                        .replace("@@pageCount@@", `<span class="dms-grid-pagecount"></span>`),
                },
            },
            groupable: {
                enabled: this.gridDefinition.CanGroupByColumns || false,
                messages: {
                    empty: this.gridDefinition.CanGroupByColumns
                        ? this.textBlockService.get("advancedGridGroupPanelText", this.textBlockCategory)
                        : "",
                },
            },
            reorderable: this.gridDefinition.CanReorderColumns || false,
            resizable: this.gridDefinition.CanResizeColumns || false,
            filterable: definition.filterable
                ? {
                      mode: "menu",
                      messages: {
                          filter: this.textBlockService.getChecked(filterTbs, "Apply", "filteroptions"),
                          and: this.textBlockService.getChecked(filterTbs, "And", "filteroptions"),
                          clear: this.textBlockService.getChecked(filterTbs, "Clear", "filteroptions"),
                          info: this.textBlockService.getChecked(filterTbs, "Info", "filteroptions"),
                          isFalse: this.textBlockService.getChecked(filterTbs, "IsFalse", "filteroptions"),
                          isTrue: this.textBlockService.getChecked(filterTbs, "IsTrue", "filteroptions"),
                          or: this.textBlockService.getChecked(filterTbs, "Or", "filteroptions"),
                          search: this.textBlockService.getChecked(filterTbs, "Search", "filteroptions"),
                          selectedItemsFormat: this.textBlockService.getChecked(filterTbs, "SelectedItemsFormat", "filteroptions"),
                          operator: this.textBlockService.getChecked(filterTbs, "Operator", "filteroptions"),
                          value: this.textBlockService.getChecked(filterTbs, "Value", "filteroptions"),
                          checkAll: this.textBlockService.getChecked(filterTbs, "CheckAll", "filteroptions"),
                      },
                      operators: {
                          string: {
                              eq: this.textBlockService.getChecked(filterTbs, "EqualTo", "filteroptions"),
                              neq: this.textBlockService.getChecked(filterTbs, "NotEqualTo", "filteroptions"),
                              isnull: this.textBlockService.getChecked(filterTbs, "IsNull", "filteroptions"),
                              isnotnull: this.textBlockService.getChecked(filterTbs, "NotIsNull", "filteroptions"),
                              isempty: this.textBlockService.getChecked(filterTbs, "IsEmpty", "filteroptions"),
                              isnotempty: this.textBlockService.getChecked(filterTbs, "NotIsEmpty", "filteroptions"),
                              startswith: this.textBlockService.getChecked(filterTbs, "StartsWith", "filteroptions"),
                              contains: this.textBlockService.getChecked(filterTbs, "Contains", "filteroptions"),
                              doesnotcontain: this.textBlockService.getChecked(filterTbs, "DoesNotContain", "filteroptions"),
                              endswith: this.textBlockService.getChecked(filterTbs, "EndsWith", "filteroptions"),
                          },
                          number: {
                              eq: this.textBlockService.getChecked(filterTbs, "EqualTo", "filteroptions"),
                              neq: this.textBlockService.getChecked(filterTbs, "NotEqualTo", "filteroptions"),
                              isnull: this.textBlockService.getChecked(filterTbs, "IsNull", "filteroptions"),
                              isnotnull: this.textBlockService.getChecked(filterTbs, "NotIsNull", "filteroptions"),
                              gte: this.textBlockService.getChecked(filterTbs, "GreaterThanOrEqualTo", "filteroptions"),
                              gt: this.textBlockService.getChecked(filterTbs, "GreaterThan", "filteroptions"),
                              lte: this.textBlockService.getChecked(filterTbs, "LessThanOrEqualTo", "filteroptions"),
                              lt: this.textBlockService.getChecked(filterTbs, "LessThan", "filteroptions"),
                          },
                          date: {
                              eq: this.textBlockService.getChecked(filterTbs, "EqualTo", "filteroptions"),
                              neq: this.textBlockService.getChecked(filterTbs, "NotEqualTo", "filteroptions"),
                              isnull: this.textBlockService.getChecked(filterTbs, "IsNull", "filteroptions"),
                              isnotnull: this.textBlockService.getChecked(filterTbs, "NotIsNull", "filteroptions"),
                              gte: this.textBlockService.getChecked(filterTbs, "AfterThanOrEqualTo", "filteroptions"),
                              gt: this.textBlockService.getChecked(filterTbs, "AfterThan", "filteroptions"),
                              lte: this.textBlockService.getChecked(filterTbs, "BeforeThanOrEqualTo", "filteroptions"),
                              lt: this.textBlockService.getChecked(filterTbs, "BeforeThan", "filteroptions"),
                          },
                      },
                  }
                : false,
            page: (e) => this.onPage(e),
            filter: (e) => this.onFilter(e),
            // change: (e) => this.onChange(e),
            sort: (e) => this.onSort(e),
            edit: (e) => this.onEdit(e),
            group: (e) => this.onGroup(e),
            scrollable: true,
            columns: definition.columns,
            dataBound: (e) => this.onDataBound(e),
            dataBinding: (e) => this.onDataBinding(e),
            columnResize: (e) => this.onColumnResize(e),
            // detailTemplate: "<div class='childgrid'></div>",
            // detailInit: (e) => this.onDetailInit(e),
            selectable: "row",
            noRecords: true,
            height: this.height || 1,
            messages: {
                noRecords: this.textBlockService.get("advancedGridNoRecordsText", this.textBlockCategory),
            },
            columnResizeHandleWidth: 5,
        };
        // (<any>options.pageable).selectTemplate = `<li><span class="k-state-selected">#=data.text#</span></li>`;
        // (<any>options.pageable).currentPageTemplate = `<li class="k-current-page"><span class="k-link k-pager-nav">#=data.text#</span></li>`;
        // (<any>options.pageable).linkTemplate = `<li><a tabindex="-1" href="\\#" class="k-link" data-#=data.ns#page="#=data.idx#" #if (data.title !== "") {# title="#=data.title#" #}#>#=data.text#</a></li>`;
        return options;
    }

    // private onDetailInit(e: kendo.ui.GridDetailInitEvent) {
    //     let detailRow = e.detailRow;

    //     let container = detailRow.find(".childgrid");

    //     CoreContext.Instance.initializeGrid(container, this.gridDefinition);
    // }

    /**
     * Handler for the error-event.
     * @param e EventArgs.
     */
    private onError(e: kendo.data.DataSourceErrorEvent): void {
        // Navigation.errorAsync(e.status, "Error");
    }

    /**
     * Handler for ColumnResize-Event
     */
    private onColumnResize(e: kendo.ui.GridColumnResizeEvent): void {
        this.trigger(GridEventTypes.ResizeRequested);
        // setTimeout(() => , 100);
    }

    /**
     * Get the dataRow for a given control.
     * @param control Control in edit-row.
     * @hidden
     */
    protected getDataRow(control: IControl): IDataRow {
        let tr = $(control.getElement()).closest("tr");
        return <IDataRow>(<any>this._kendoElement.dataItem(tr));
    }

    private _scrollTop: number = null;
    private _scrollLeft: number = null;

    private onExportButton(): void {
        this.setBusy(true);
        let service = this.exportService;
        let columns = this.getExportColumns();
        let filter = CoreContext.Instance.getFilter();
        service
            .export(
                this.gridDefinition.GridID,
                filter != null ? filter.getGridId() : null,
                this.textBlockCategory,
                this.commandOption,
                columns
            )
            .then(() => {
                this.setBusy(false);
            });
    }

    private getExportColumns(): IExportColumn[] {
        let result: IExportColumn[] = [];
        let i = 0;
        for (const column of this._kendoElement.columns) {
            result.push({
                Name: column.field,
                IsHidden: column.hidden,
                DisplayIndex: i,
            });
            i++;
        }
        return result;
    }

    /**
     * Set the grid in add-mode. Either show the add row immediately or show the add row on top.
     */
    private onAddButton(): void {
        this.disposeEditContext();
        this.editContext.initialize(EditModes.Add);

        // if the add line is shown immediately, just edit the dummy row
        if (this.gridDefinition.AddAllowed && this.gridDefinition.ShowAddLineImmediately && this.insertRowModel) {
            this.saveScrollPosition();
            this._kendoElement.editRow(this.insertRowModel);
        } else {
            // otherwise just use the grids add functionality
            this._kendoElement.addRow();
        }
    }

    // private getFirstValueControl(columnDefinition: IGridColumnDefinition): IControlDefinition {
    //     for (let ctrIdx = 0, ctrLen = columnDefinition.Controls.length; ctrIdx < ctrLen; ctrIdx++) {
    //         let control = columnDefinition.Controls[ctrIdx];
    //         if (!Util.stringIsNullOrEmpty(control.ValueColumnName) && control.ControlTypeID != GridControlTypes.FileUpload) {
    //             return control;
    //         }
    //     }
    //     return null;
    // }

    /**
     * Create gridColumns and fieldDefinitions from gridDefinition.
     */
    private getColumnsAndFields(
        userConfig: IGridUserConfig
    ): {
        fields: kendo.data.DataSourceSchemaModelFields;
        columns: kendo.ui.GridColumn[];
        modelExtensions: Object;
        groupingFields: kendo.data.DataSourceGroupItem[];
        filterable: boolean;
        aggregateItems: kendo.data.DataSourceAggregateItem[];
        sortingFields: kendo.data.DataSourceSortItem[];
    } {
        let fields: kendo.data.DataSourceSchemaModelFields = {};
        let columns: kendo.ui.GridColumn[] = [];
        let modelExtensions: Object = {};
        let groupingFields: kendo.data.DataSourceGroupItem[] = [];
        let filterable = false;
        let aggregateItems: kendo.data.DataSourceAggregateItem[] = [];
        let columnGroups: { [groupId: number]: kendo.ui.GridColumn } = {};

        // if the add row is added by the grid, set the default value of the RowIndex to -1
        if (this.gridDefinition.AddAllowed && !this.gridDefinition.ShowAddLineImmediately) {
            fields[DataConstants.ROW_INDEX_COLUMN_NAME] = {
                nullable: false,
                type: "number",
                defaultValue: -1,
            };
        }

        let columnMeasurementElement = $("<div class='dms-column-width'/>");
        this.jQueryElement.append(columnMeasurementElement);

        for (let colIndex = 0, colLen = this.gridDefinition.Columns.length; colIndex < colLen; colIndex++) {
            let columnDefinition = this.gridDefinition.Columns[colIndex];

            this.columnDefinitionsByName[columnDefinition.ColumnName] = columnDefinition;
            let template = "";
            let groupHeaderTemplate = "";
            let filterItemTemplate: Function = null;
            let filterControlId: number;
            let filterControl: IControl;

            let aggregateControlId: number;
            // let aggregateControl: IControl;
            let aggregateFunction: string;
            let aggregateTemplate: string;
            let fieldNames: string[] = [];
            let renderControls =
                columnDefinition.ColumnTypeID != ColumnTypes.Submitbutton &&
                columnDefinition.ColumnTypeID != ColumnTypes.Button &&
                columnDefinition.ColumnTypeID != ColumnTypes.Group; // Submitbuttons are rendered in Toolbar
            if (columnDefinition.ColumnFilterTypeID && !filterable) {
                filterable = true;
            }
            for (let ctrIndex = 0, ctrLen = columnDefinition.Controls.length; ctrIndex < ctrLen; ctrIndex++) {
                let controlDefinition = columnDefinition.Controls[ctrIndex];

                this.controlDefinitionDict[controlDefinition.ControlID] = controlDefinition; // save the controlDefininition per ControlID to find it faster later.

                let control = CoreContext.Instance.initializeControl(controlDefinition, this.textBlockCategory);

                if (ctrIndex == 0) {
                    this.firstTextColumnForColumnName[columnDefinition.ColumnName] = control.getTextColumnName();
                    groupHeaderTemplate = control.getGroupHeaderTemplate();
                }

                ///
                // generate fields
                ///
                let dataType = control.getModelDatatype();

                let fieldName = this.getControlFieldName(controlDefinition);
                if (fields[fieldName] == undefined) {
                    // field not already existing
                    if (fieldName) {
                        fields[fieldName] = {
                            nullable: false, //nullable: Util.stringIsNullOrEmpty(controlDefinition.DefaultValue),
                            type: dataType,
                            defaultValue: () => control.getDefaultValue(),
                        };
                    }
                    let textColumnFieldName = controlDefinition.TextColumnName;
                    if (textColumnFieldName) {
                        if (fields[textColumnFieldName] == null) {
                            fields[textColumnFieldName] = {
                                nullable: true,
                                type: "string",
                                defaultValue: null,
                            };
                        }
                    }
                } else {
                    // field already existing
                    if (fields[fieldName].type == "string") {
                        fields[fieldName].type = dataType;
                    }
                }

                if (columnDefinition.ColumnTypeID == ColumnTypes.Group && columnDefinition.GroupColumnName) {
                    groupingFields.push({
                        field: columnDefinition.ColumnName, // use the column name here. the actual group column name will be set in the read method
                    });

                    let title = this.textBlockService.get(columnDefinition.ColumnHeader, this.textBlockCategory);
                    let titleText = Util.stripHTML(title);
                    let gridColumn: kendo.ui.GridColumn = {
                        field: columnDefinition.ColumnName,
                        groupHeaderTemplate: titleText + ": " + groupHeaderTemplate,
                        headerTemplate: title == "" ? " " : title,
                        title: titleText,
                        hidden: true,
                        menu: false,
                    };
                    columns.push(gridColumn);
                    this.firstTextColumnForColumnName[columnDefinition.ColumnName] = control.getTextColumnName();
                    continue;
                }

                ///
                // generate control template
                ///
                if (renderControls && (controlDefinition.OnDisplay == null || controlDefinition.OnDisplay)) {
                    // if (this.incellEditing && (
                    //     controlDefinition.ControlTypeID == GridControlTypes.Edit ||
                    //     controlDefinition.ControlTypeID == GridControlTypes.Delete)) {
                    //     continue;
                    // }
                    let templ = control.getTemplate(DisplayModes.Display, fieldName);
                    if (templ != null) {
                        if (fieldName) {
                            fieldNames.push(fieldName);
                        }
                        // in case the add line is shown immediately, we need to hide all default values in the dummy row
                        // this is only necessary if the control is bound to a value and it has a default value defined or when the control is an edit-button.
                        if (
                            this.gridDefinition.ShowAddLineImmediately &&
                            this.gridDefinition.AddAllowed &&
                            (controlDefinition.ControlTypeID == GridControlTypes.Edit ||
                                controlDefinition.ControlTypeID == GridControlTypes.Link ||
                                controlDefinition.ControlTypeID == GridControlTypes.Image ||
                                controlDefinition.ControlTypeID == GridControlTypes.Command ||
                                controlDefinition.ControlTypeID == GridControlTypes.Delete ||
                                controlDefinition.ControlTypeID == GridControlTypes.Checkbox ||
                                controlDefinition.ControlTypeID == GridControlTypes.ContextMenu ||
                                controlDefinition.ControlTypeID == GridControlTypes.DatabaseImage ||
                                controlDefinition.ControlTypeID == GridControlTypes.List ||
                                controlDefinition.ControlTypeID == GridControlTypes.ComboBox ||
                                controlDefinition.ControlTypeID == GridControlTypes.Text ||
                                !Util.stringIsNullOrEmpty(controlDefinition.DefaultValue))
                        ) {
                            template += `# if(${DataConstants.ROW_INDEX_COLUMN_NAME} !== -1) { #${templ.template}# } else { #<span>&nbsp;</span># } #`;
                        } else {
                            template += templ.template;
                        }
                        if (templ.modelExtension != null) {
                            $.extend(modelExtensions, templ.modelExtension);
                        }
                    }
                }

                ///
                /// generate aggregation info
                ///
                if (columnDefinition.AggregateFunctionID && aggregateControlId == null) {
                    if (
                        columnDefinition.AggregateControlID == undefined ||
                        (columnDefinition.AggregateControlID == controlDefinition.ControlID &&
                            controlDefinition.ValueColumnName &&
                            controlDefinition.ControlTypeID == GridControlTypes.Number)
                    ) {
                        this.firstAggregateColumnForColumnName[columnDefinition.ColumnName] = controlDefinition.ValueColumnName;
                        aggregateControlId = controlDefinition.ControlID;
                        // aggregateControl = control;
                        switch (columnDefinition.AggregateFunctionID) {
                            case AggregateFunctions.Average:
                                aggregateFunction = "average";
                                break;
                            case AggregateFunctions.Count:
                                aggregateFunction = "count";
                                break;
                            case AggregateFunctions.Max:
                                aggregateFunction = "max";
                                break;
                            case AggregateFunctions.Min:
                                aggregateFunction = "min";
                                break;
                            case AggregateFunctions.Sum:
                                aggregateFunction = "sum";
                                break;
                        }
                        aggregateTemplate = control.getDisplayTemplateForField(aggregateFunction);
                        aggregateItems.push({ field: columnDefinition.ColumnName, aggregate: aggregateFunction });
                    }
                }

                ///
                /// generate filter template for checkbox filtering
                ///
                if (columnDefinition.ColumnFilterTypeID && filterControlId == null) {
                    if (
                        (columnDefinition.ColumnFilterControlID == undefined ||
                            columnDefinition.ColumnFilterControlID == controlDefinition.ControlID) &&
                        controlDefinition.ValueColumnName
                    ) {
                        this.firstFilterColumnForColumnName[columnDefinition.ColumnName] = controlDefinition.ValueColumnName;
                        filterControlId = controlDefinition.ControlID;
                        filterControl = control;
                        if (columnDefinition.ColumnFilterTypeID == ColumnFilterTypes.DropDown) {
                            filterItemTemplate = (e: any) => {
                                if (e.field == "all") {
                                    return `<li class="k-item"><label class="k-label"><input type="checkbox" class="k-check-all">&nbsp;#= data.all #</label></li>`;
                                } else {
                                    return `<li class="k-item"><label class="k-label"><input type="checkbox" value="#= data.get('${
                                        controlDefinition.ValueColumnName
                                    }')#"/>&nbsp;${control.getDisplayTemplateForField("__dms_Filter_Text")}</label></li>`;
                                }
                            };
                        }
                    }
                }
            }

            // column selector
            if (
                columnDefinition.ColumnTypeID == ColumnTypes.ColumnSelector &&
                columnDefinition.Controls.length > 0 &&
                columnDefinition.Controls[0].ControlTypeID == GridControlTypes.Checkbox
            ) {
                if (this.getGridType() == GridTypes.EasyEdit) {
                    let columnCssClass = this.getColumnCssClass(columnDefinition);
                    let controlDefinition = columnDefinition.Controls[0];

                    let template = `<div class="dms-control-checkbox"><input type="checkbox" data-dms-id="${controlDefinition.ControlID}" id="dms-columnselector-${controlDefinition.ControlID}-#=data.get('__RowIndex')#" class="k-checkbox dms-columnselector" #=(data.get('${controlDefinition.ValueColumnName}') ? 'checked="checked"' : '')#><label class="k-checkbox-label" for="dms-columnselector-${controlDefinition.ControlID}-#=data.get('__RowIndex')#"></label></div>`;

                    if (controlDefinition.RangeColumnName) {
                        // function for model binding
                        let propertyName = CONTROL_VISIBILITY_PROPERTY_NAME + controlDefinition.ControlID;

                        // create the function
                        $.extend(modelExtensions, {
                            [propertyName]: function () {
                                return (
                                    this.get(DataConstants.ROW_INDEX_COLUMN_NAME) == -1 || // range not valid in add mode
                                    Util.isValueInRange(
                                        this.get(controlDefinition.RangeColumnName),
                                        controlDefinition.RangeFrom,
                                        controlDefinition.RangeTo,
                                        null,
                                        controlDefinition.RangeInvert
                                    )
                                );
                            },
                        });
                        template = `# if(${propertyName}()) { # ${template} # } #`;
                    }

                    let col: kendo.ui.GridColumn = {
                        field: columnDefinition.ColumnName,
                        width: this.getColumnWidth(columnDefinition, columnMeasurementElement),
                        headerTemplate: `<div class="dms-control-checkbox"><input type="checkbox" class="k-checkbox dms-columnselector-header" data-dms-id="${controlDefinition.ControlID}" id="dms-columnselector-header-${controlDefinition.ControlID}" /><label class="k-checkbox-label" for="dms-columnselector-header-${controlDefinition.ControlID}" /></div>`,
                        groupable: false,
                        sortable: false,
                        template: template,
                        headerAttributes: {
                            class: columnCssClass,
                        },
                        attributes: {
                            class: columnCssClass,
                        },
                        editable: function (e: any) {
                            return false;
                        },
                        menu: false,
                        media: columnDefinition.VisibleMedia,
                        locked: columnDefinition.IsFrozen && this.gridDefinition.CanFreezeColumns,
                        lockable: false,
                    };
                    columns.push(col);
                    this.columnSelectorColumnNames.push(controlDefinition.ValueColumnName);
                }
                continue;
            }

            template = this.modifyColumnTemplate(template, fieldNames);

            // add Column only if there are controls in it
            if (renderControls) {
                let hasField =
                    columnDefinition.Controls[0].ValueColumnName != null &&
                    columnDefinition.Controls[0].ValueColumnName != "" &&
                    columnDefinition.Controls[0].ControlTypeID != GridControlTypes.FileUpload;
                let field = columnDefinition.ColumnName;

                let columnCssClass = this.getColumnCssClass(columnDefinition);
                let title = this.textBlockService.get(columnDefinition.ColumnHeader, this.textBlockCategory);
                let aggregates: any[] = [];

                if (aggregateFunction) {
                    aggregates.push(aggregateFunction);
                }

                let titleText = Util.stripHTML(title);

                let headerTemplate = `<span class="dms-tooltip" data-dms-tooltip="${Util.encode(
                    this.textBlockService.get(columnDefinition.Tooltip || columnDefinition.ColumnHeader, this.textBlockCategory),
                    EncodingTypes.HtmlAttribute
                )}">${title == "" ? " " : title}</span>`;
                if (columnDefinition.HelpTextBlockName) {
                    headerTemplate += `<span class="dms-helptext fa fa-question-circle" data-dms-helptext="${Util.encode(
                        this.textBlockService.get(columnDefinition.HelpTextBlockName, this.textBlockCategory),
                        EncodingTypes.HtmlAttribute
                    )}"></span>`;
                }

                let gridColumn: kendo.ui.GridColumn = {
                    field: field,
                    headerTemplate: headerTemplate,
                    title: titleText,
                    width: this.getColumnWidth(columnDefinition, columnMeasurementElement),
                    // template: (e: any, options: any) => {
                    //     return kendo.template(template)(e);
                    // },
                    template: template,
                    sortable: hasField,
                    groupable: hasField,
                    filterable: false,
                    aggregates: aggregates,
                    groupFooterTemplate:
                        (columnDefinition.GroupFooterTextBlockName
                            ? this.textBlockService.get(columnDefinition.GroupFooterTextBlockName)
                            : "") + (aggregateTemplate || ""),
                    footerTemplate:
                        (columnDefinition.GroupFooterTextBlockName
                            ? this.textBlockService.get(columnDefinition.GroupFooterTextBlockName)
                            : "") + (aggregateTemplate || ""),
                    groupHeaderTemplate: titleText + ": " + groupHeaderTemplate,
                    locked: columnDefinition.IsFrozen && this.gridDefinition.CanFreezeColumns,
                    lockable: false,
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.getEditor(columnDefinition, container[0], options);
                    },
                    headerAttributes: {
                        class: columnCssClass,
                    },
                    footerAttributes: {
                        class: columnCssClass,
                    },
                    attributes: {
                        class: columnCssClass,
                    },
                    editable: (e: IDataRow) => this.onColumnEditable(columnDefinition, e),
                    media: columnDefinition.VisibleMedia,
                };

                if (columnDefinition.ColumnFilterTypeID == ColumnFilterTypes.DropDown && filterItemTemplate) {
                    let filterDataSource: kendo.data.DataSourceOptions = {
                        serverPaging: true,
                        transport: {
                            read: (options: kendo.data.DataSourceTransportOptions) => {
                                this.commandOption = options.data;
                                this.commandService.getFilter(this.gridDefinition.GridID, filterControlId, options.data).then((result) => {
                                    (options.success as JQuery.Ajax.SuccessCallback<any>)(result, null, null);
                                });
                            },
                        },
                        schema: {
                            data: function (data: IODataResult) {
                                return data.value;
                            },
                            total: function (data: IODataResult) {
                                return data["odata.count"];
                            },
                        },
                    };
                    gridColumn.filterable = <kendo.ui.GridColumnFilterable>{
                        multi: true,
                        dataSource: new kendo.data.DataSource(filterDataSource),
                        itemTemplate: filterItemTemplate,
                        search: true,
                    };
                    (<any>gridColumn.filterable).field = this.controlDefinitionDict[filterControlId].ValueColumnName;
                } else if (columnDefinition.ColumnFilterTypeID == ColumnFilterTypes.Text) {
                    gridColumn.filterable = <kendo.ui.GridColumnFilterable>{
                        // ui: "numerictextbox",
                        // operators: { string: { eq: "test" } }
                    };
                    let ui: Function = null;
                    // TODO: Refactor creation of this controls:
                    switch (this.controlDefinitionDict[filterControlId].ControlTypeID) {
                        case GridControlTypes.Number:
                            ui = (element: JQuery) => {
                                let controlDefinition = this.controlDefinitionDict[filterControlId];
                                new kendo.ui.NumericTextBox(element[0], {
                                    format: (<NumberControl>filterControl).getFormat(),
                                    spinners: false,
                                    decimals: controlDefinition.DecimalDigits,
                                    max: controlDefinition.MaxValue,
                                    min: controlDefinition.MinValue,
                                });
                            };
                            break;
                        case GridControlTypes.Date:
                            ui = (element: JQuery) => {
                                let dataBind = element.attr("data-bind");
                                element.attr("data-bind", dataBind.replace("value:", "customDate:"));
                                let datePicker = new kendo.ui.DatePicker(element[0], {
                                    format: CoreContext.Environment.DateFormat,
                                    culture: CoreContext.Environment.Culture,
                                    dateInput: true,
                                });
                                (<any>datePicker)._dateInput.setOptions({
                                    messages: {
                                        month: "mm",
                                        day: "dd",
                                        year: "yyyy",
                                    },
                                });
                            };
                            break;
                        case GridControlTypes.DateTime:
                            ui = (element: JQuery) => {
                                let datetimepicker = new kendo.ui.DateTimePicker(element[0], {
                                    format: CoreContext.Environment.DateTimeFormat,
                                    culture: CoreContext.Environment.Culture,
                                    dateInput: true,
                                });
                                (<any>datetimepicker)._dateInput.setOptions({
                                    messages: {
                                        month: "mm",
                                        day: "dd",
                                        year: "yyyy",
                                        hour: "hh",
                                        minute: "mm",
                                    },
                                });
                            };
                            break;
                        case GridControlTypes.Time:
                            ui = (element: JQuery) => {
                                let timepicker = new kendo.ui.TimePicker(element[0], {
                                    culture: CoreContext.Environment.Culture,
                                    format: CoreContext.Environment.TimeFormat,
                                    dateInput: true,
                                });
                                (<any>timepicker)._dateInput.setOptions({
                                    messages: {
                                        hour: "hh",
                                        minute: "mm",
                                        second: "ss",
                                    },
                                });
                            };
                            break;
                    }
                    if (ui) {
                        gridColumn.filterable.ui = ui;
                    }
                    (<any>gridColumn.filterable).field = filterControl.getTextColumnName();
                }

                if (columnDefinition.GroupID) {
                    let groupColumn = columnGroups[columnDefinition.GroupID];
                    if (!groupColumn) {
                        groupColumn = {
                            title: this.textBlockService.get(columnDefinition.GroupHeader, this.textBlockCategory),
                            columns: [gridColumn],
                        };
                        columnGroups[columnDefinition.GroupID] = groupColumn;
                        columns.push(groupColumn);
                    } else {
                        groupColumn.columns.push(gridColumn);
                    }
                } else {
                    columns.push(gridColumn);
                }
            }
        }

        if (aggregateItems.length > 0 && groupingFields.length > 0) {
            for (let groupIdx = 0, groupLen = groupingFields.length; groupIdx < groupLen; groupIdx++) {
                let groupingField = groupingFields[groupIdx];
                groupingField.aggregates = aggregateItems;
            }
        }

        columns.push({ title: "" });
        columnMeasurementElement.remove();

        let sortingFields: kendo.data.DataSourceSortItem[] = [];
        if (this.gridDefinition.Order) {
            for (let i = 0, len = this.gridDefinition.Order.length; i < len; i++) {
                let orderDefinition = this.gridDefinition.Order[i];
                sortingFields.push({
                    field: orderDefinition.ColumnName,
                    dir: orderDefinition.Descending ? "desc" : "asc",
                });
                if (orderDefinition.Required) {
                    this.sortRequiredFields[orderDefinition.ColumnName] = true;
                }
            }
        }

        if (userConfig) {
            groupingFields = userConfig.GroupDefinition || [];
            sortingFields = userConfig.SortDefinition || [];
            this.sortRequiredFields = userConfig.SortRequiredFields || {};

            let configColumns: kendo.ui.GridColumn[] = [];
            let columnByField: { [field: string]: kendo.ui.GridColumn } = {};
            for (let i = 0, len = columns.length; i < len; i++) {
                columnByField[columns[i].field] = columns[i];
            }
            for (let i = 0, len = userConfig.Columns.length; i < len; i++) {
                let userConfigColumn = userConfig.Columns[i];
                let gridColumn = columnByField[userConfigColumn.ColumnName];
                if (gridColumn) {
                    gridColumn.hidden = userConfigColumn.IsHidden;
                    gridColumn.width = userConfigColumn.Width;
                    configColumns.push(gridColumn);
                    delete columnByField[userConfigColumn.ColumnName];
                }
            }

            let keys = Object.keys(columnByField);
            for (let i = 0, len = keys.length; i < len; i++) {
                let column = columnByField[keys[i]];
                configColumns.push(column);
            }
            columns = configColumns;
        }

        return {
            fields: fields,
            columns: columns,
            modelExtensions: modelExtensions,
            groupingFields: groupingFields,
            filterable: filterable,
            aggregateItems: aggregateItems,
            sortingFields: sortingFields,
        };
    }

    /**
     * Modify the created column template.
     * @param template Default created template for the grid.
     * @param fieldNames All field names in this column.
     * @hidden
     */
    protected modifyColumnTemplate(template: string, fieldNames: string[]): string {
        return template;
    }

    /**
     * Gets the CssClass for a given ColumnDefinition.
     * @param columnDefinition ColumnDefinition
     * @hidden
     */
    protected getColumnCssClass(columnDefinition: IGridColumnDefinition): string {
        let cssClass = super.getColumnCssClass(columnDefinition);

        // handling of ellipsis / wordbreaking
        if (columnDefinition.UseEllipsis) {
            cssClass += " dms-column-ellipsis";
        } else if (columnDefinition.UseWordBreaking) {
            cssClass += " dms-column-wordbreaking";
        }
        return cssClass;
    }

    /**
     * Get the pagesizes array.
     */
    private getPageSizes(): Array<Object> {
        let array = [10, 25, 50, 100, "all"];
        let pageSize = this.gridDefinition.PageSize || 50;
        if (array.indexOf(pageSize) == -1) {
            array.unshift(pageSize);
            array.sort(function (a: any, b: any) {
                return a - b;
            });
        }
        return array;
    }

    /**
     * Check if column is editable.
     * @param columnDefinition ColumnDefinition.
     * @param dataRow Current datarow.
     * @hidden
     */
    protected onColumnEditable(columnDefinition: IGridColumnDefinition, dataRow: IDataRow) {
        return true;
    }

    ///
    /// Grid Events for EditContext
    ///

    /**
     * Event occurs when user sorts a grid column.
     * @param e EventArgs.
     */
    private onSort(e: kendo.ui.GridSortEvent): void {
        let columnName = e.sort.field;

        let columnDefinition = this.columnDefinitionsByName[columnName];
        let sortField = columnDefinition.Controls[0].ValueColumnName;

        // if the sort definition is defined in dms as required, do not sort the column.
        if (this.sortRequiredFields[sortField] === true) {
            e.preventDefault();
        }

        e.sort.field = sortField;
        this._saveScrollPos = true;
        this.saveScrollPosition();

        this.disposeEditContext();
        setTimeout(() => {
            this.trigger(GridEventTypes.ResizeRequested);
        }, 100);
    }

    /**
     * @hidden
     */
    protected saveScrollPosition(): void {
        if (this._saveScrollPos && this._scrollTop == null) {
            this._scrollTop = this._kendoElement.content[0].scrollTop;
            this._scrollLeft = this._kendoElement.content[0].scrollLeft;
            this._saveScrollPos = false;
        }
    }

    protected restoreScrollPosition() {
        if (this._kendoElement && this._kendoElement.content) {
            if (this._scrollTop != null) {
                this._kendoElement.content[0].scrollTop = this._scrollTop;
                this._scrollTop = null;
            }
            if (this._scrollLeft != null) {
                this._kendoElement.content[0].scrollLeft = this._scrollLeft;
                this._scrollLeft = null;
            }
        }
    }

    /**
     * Event occurs when user pages the grid.
     * @param e EventArgs.
     */
    protected onPage(e: kendo.ui.GridPageEvent): void {
        this._saveScrollPos = false;
        this.disposeEditContext();
    }

    private onFilter(e: kendo.ui.GridFilterEvent): void {
        this.disposeEditContext();
    }

    /**
     * Event occurs when user groups a grid column.
     * @param e EventArgs.
     */
    private onGroup(e: kendo.ui.GridGroupEvent): void {
        this.disposeEditContext();
        // this.setGroupIndicator();
    }

    /**
     * Event occurs when user edits a row.
     * @param e EventArgs.
     */
    protected onEdit(e: kendo.ui.GridEditEvent): void {
        this.currentEditUid = e.model.uid;
        this.editContext.setDataRowFunction(() => {
            let data = <IDataRow>(<any>e.model); // <any>: avoid telerik to dms structure cast problem
            if (this.editContext.editMode == EditModes.Add) {
                data = $.extend(data, this.fixedValues);
            }
            return data;
        });
        // all controls are loaded -> startup editContext
        this.editContext.load(this.textBlockCategory, e.container).then(() => {
            this.trigger(GridEventTypes.ControlDataLoaded);
        });
        this.restoreScrollPosition();
    }

    /**
     * Event occurs before grid data was bound.
     * @param e EventArgs.
     * @hidden
     */
    protected onDataBinding(e: kendo.ui.GridDataBindingEvent) {}

    /**
     * Event occurs after grid data was bound.
     * @param e EventArgs.
     * @hidden
     */
    protected onDataBound(e: kendo.ui.GridDataBoundEvent): void {
        // data was filtered on a page and no items are left on the selected page -> go to page 1
        if (
            this._kendoElement.dataSource.totalPages() > 0 &&
            this._kendoElement.dataSource.page() > this._kendoElement.dataSource.totalPages()
        ) {
            this._kendoElement.dataSource.page(1);
            return;
        }

        this.handleDataBound(this._kendoElement.content, true);
        if (this._kendoElement.lockedContent) {
            this.handleDataBound(this._kendoElement.lockedContent);
        }

        // render the dummy add line as editable
        if (this.gridDefinition.ShowAddLineImmediately && this.gridDefinition.AddAllowed) {
            this.onAddButton();
            // if the data is grouped and the addline is shown, we need to hide the first grouping row.
            this.jQueryElement.find(".k-grouping-row:first").hide();
            this.jQueryElement.find(".k-group-footer:first").hide();
        }
        this.restoreScrollPosition();

        // setTimeout because the pager is rendered after the dataBound-event.
        setTimeout(() => {
            if (this._kendoElement && this._kendoElement.pager && this._kendoElement.pager.element) {
                let pageCountElement = this._kendoElement.pager.element.find(".dms-grid-pagecount");
                if (pageCountElement.length > 0) {
                    let pages = this._kendoElement.dataSource.totalPages().toString();
                    pageCountElement.text(pages);
                }
            }
        }, 10);

        // setTimeout(() => {
        //     this.setGroupIndicator();
        // }, 10);

        // hack for kendo bug in v2018.1.221
        if (this._kendoElement.dataSource.pageSize() == this._kendoElement.dataSource.total()) {
            (<any>this._kendoElement.dataSource)._pageSize =
                (this.userConfig && this.userConfig.PageSize) || this.gridDefinition.PageSize || 50;
        }

        this.trigger(GridEventTypes.DataBound, new EventArgs(this, null));
        setTimeout(() => {
            this.trigger(GridEventTypes.ResizeRequested);
        });
    }

    // private setGroupIndicator(): void {
    // let groupContainer: JQuery = (<any>this._kendoElement).groupable.groupContainer;

    // for (let groupIdx = 0, groupLen = this._kendoElement.dataSource.group().length; groupIdx < groupLen; groupIdx++) {
    //     let group = this._kendoElement.dataSource.group()[groupIdx];
    //     let groupIndicator = groupContainer.find(`.k-group-indicator[data-field='${group.field}'] .k-link`);

    //     let column: kendo.ui.GridColumn;
    //     for (let colIdx = 0, colLen = this._kendoElement.columns.length; colIdx < colLen; colIdx++) {
    //         let col = this._kendoElement.columns[colIdx];
    //         if (col.field == group.field) {
    //             column = col;
    //             break;
    //         }
    //     }

    //     let currentHtml = groupIndicator.html();
    //     let iconLastIndex = currentHtml.indexOf("</span>");
    //     groupIndicator.html(currentHtml.substring(0, iconLastIndex + 7) + column.headerTemplate);
    // }
    // }

    /**
     * Handle functionality which occurs after dataBinding of the grid.
     * @param rootElement
     */
    private handleDataBound(rootElement: JQuery, applyGridFormat: boolean = false): void {
        // register events to rendered buttons.
        // TODO: refactor this method:
        this.initializeRowControls(
            rootElement,
            "." + CssClass.delete,
            null,
            (e) => this.beforeCommand(),
            (e) => this.afterDelete(e.sender),
            (e) => this.afterCommandCancel()
        );
        this.initializeRowControls(rootElement, "." + CssClass.edit, (s) => this.edit(s), null, null, null);
        this.initializeRowControls(rootElement, "." + CssClass.link, null, null, null, null);
        this.initializeRowControls(rootElement, "." + CssClass.textblock, null, null, null, null);
        this.initializeRowControls(rootElement, "." + CssClass.command, null, null, null, null);
        this.initializeRowControls(rootElement, "." + CssClass.image, null, null, null, null);
        this.initializeRowControls(rootElement, "." + CssClass.contextMenu, null, null, null, null);

        if (applyGridFormat) {
            // go through all rows and apply gridformat to rows
            if (this.gridDefinition.FormatDefinitions && this.gridDefinition.FormatDefinitions.length > 0) {
                let rows = this._kendoElement.tbody.children();
                for (let j = 0, len = rows.length; j < len; j++) {
                    let row = $(rows[j]);
                    let dataItem = this._kendoElement.dataItem(row);

                    if (dataItem && (<any>dataItem).__dms_rowCssClass) {
                        let css = (<any>dataItem).__dms_rowCssClass();
                        if (css) {
                            row.addClass(css);
                        }
                    }
                }

                if (this._kendoElement.lockedContent) {
                    rows = this._kendoElement.lockedContent.find("tbody").children();
                    for (let j = 0, len = rows.length; j < len; j++) {
                        let row = $(rows[j]);
                        let dataItem = this._kendoElement.dataItem(row);

                        if (dataItem && (<any>dataItem).__dms_rowCssClass) {
                            let css = (<any>dataItem).__dms_rowCssClass();
                            if (css) {
                                row.addClass(css);
                            }
                        }
                    }
                }
            }
        }
        this.initializeTooltips();
    }

    // Initializes all buttons in the given grid with the given cssClass and applies the onClickFunction
    private initializeRowControls(
        rootElement: JQuery,
        cssClass: string,
        onClickFunction: (control: IControl) => void,
        beforeCommandFunction: (e: EventArgs<IControl>) => void,
        afterCommandFunction: (e: EventArgs<IControl>) => void,
        afterCommandCancelFunction: (e: EventArgs<IControl>) => void
    ): void {
        let buttons = rootElement.find(cssClass); // get all buttons with the given css class from the grid

        for (let i = 0, cnt = buttons.length; i < cnt; i++) {
            let button = buttons[i];
            let jQueryButton = $(button);
            let controlID = parseInt(jQueryButton.attr(CONTROL_ID_ATTRIBUTE_NAME));
            let controlDefinition = this.controlDefinitionDict[controlID];
            let buttonElement = CoreContext.Instance.initializeControl(controlDefinition, this.textBlockCategory);
            buttonElement.on(ControlEventTypes.Resolve, (e) => this.onResolveScript(e.sender, <string>e.data));
            buttonElement.on(ControlEventTypes.GetDataRow, (e) => this.getDataRow(e.sender));
            buttonElement.setElement(jQueryButton);
            if (onClickFunction != null) {
                buttonElement.on(ControlEventTypes.Click, (e) => onClickFunction(e.sender));
            }

            if (beforeCommandFunction != null) {
                buttonElement.on(ControlEventTypes.BeforeCommand, beforeCommandFunction);
            }

            if (afterCommandFunction != null) {
                buttonElement.on(ControlEventTypes.AfterCommand, afterCommandFunction);
            }

            if (afterCommandCancelFunction != null) {
                buttonElement.on(ControlEventTypes.AfterCommandCancel, afterCommandCancelFunction);
            }

            this.setRowControlEvent(buttonElement, cssClass);
        }
    }

    /**
     * @hidden
     */
    protected setRowControlEvent(control: IControl, cssClass: string) {}

    /**
     * Event occurs when a control requests to resolve a script.
     * @param control Control which requested resolving.
     * @param script The script which needs to be resolved.
     */
    private onResolveScript(control: IControl, script: string): string {
        let data = this.getDataRow(control);
        return super.resolve(script, data);
    }

    /**
     * Get the width for specific column.
     * @param column ColumnDefinition.
     * @param columnMeasurementElement Helper element for measurement.
     */
    private getColumnWidth(column: IGridColumnDefinition, columnMeasurementElement: JQuery): number {
        // 1. configured width in column.
        if (!Util.stringIsNullOrEmpty(column.ColumnWidth)) {
            columnMeasurementElement.width(column.ColumnWidth);
            return columnMeasurementElement.width(); // convert everything to px
        }

        // 2. configured width in controls (add control widths + padding)
        let length = column.Controls.length;
        columnMeasurementElement.width(0); // reset element
        for (let i = 0; i < length; i++) {
            let control = column.Controls[i];
            if (control.Width) {
                columnMeasurementElement.css("width", "+=" + control.Width);
                columnMeasurementElement.css("width", "+=2.5em"); // add padding
                if (control.RadioGroup) {
                    columnMeasurementElement.css("width", "+=30px"); // add space for radio button
                }
            } else if (
                control.ControlTypeID == GridControlTypes.Edit ||
                control.ControlTypeID == GridControlTypes.Delete ||
                control.ControlTypeID == GridControlTypes.Cancel ||
                control.ControlTypeID == GridControlTypes.Command ||
                (control.ControlTypeID == GridControlTypes.Link && control.Icon)
            ) {
                // icon buttons
                columnMeasurementElement.css("width", "+=60px");
            } else if (control.ControlTypeID == GridControlTypes.FileUpload) {
                columnMeasurementElement.css("width", "+=25em");
            }
        }
        if (columnMeasurementElement.width() > 0) {
            // control with width found
            return columnMeasurementElement.width();
        }

        // 3. default column width
        return 200;
    }

    /**
     * Creates the toolbar for the grid.
     * The toolbar contains the add-button if enabled and columns of the type SubmitButton and Button.
     */
    private getToolbar(): kendo.ui.GridToolbarItem[] {
        let columns: kendo.ui.GridToolbarItem[] = [];

        if (this.gridDefinition.AddAllowed && !this.gridDefinition.ShowAddLineImmediately) {
            columns.push({
                template: `<button class='k-button k-primary k-button-icontext dms-control dms-control-button ${
                    CssClass.addToolbar
                }'><span class='k-icon k-i-add'></span>${this.textBlockService.get(
                    "advancedGridAddNewRecordText",
                    this.textBlockCategory
                )}</button>`,
            });
        }
        if (this.gridDefinition.AddLinks) {
            let addLinksLength = this.gridDefinition.AddLinks.length;
            for (let i = 0; i < addLinksLength; i++) {
                columns.push({
                    template: `<button class="k-button k-primary k-button-icontext dms-control dms-control-button" onclick='DMS.Navigation.openWindow("${
                        this.gridDefinition.AddLinks[i].Url
                    }","${this.gridDefinition.AddLinks[i].UrlMode}")'><span class='k-icon k-i-add'></span>${this.textBlockService.get(
                        this.gridDefinition.AddLinks[i].TextBlock,
                        this.textBlockCategory
                    )}</button>`,
                });
            }
        }

        let submitButtons: kendo.ui.GridToolbarItem[] = [];
        let buttons: kendo.ui.GridToolbarItem[] = [];
        for (let colIndex = 0, colLen = this.gridDefinition.Columns.length; colIndex < colLen; colIndex++) {
            let column = this.gridDefinition.Columns[colIndex];

            // add submitbuttons and buttons
            if (column.ColumnTypeID == ColumnTypes.Submitbutton || column.ColumnTypeID == ColumnTypes.Button) {
                for (let ctrIndex = 0, ctrLen = column.Controls.length; ctrIndex < ctrLen; ctrIndex++) {
                    let control = column.Controls[ctrIndex];
                    let ctrl = CoreContext.Instance.initializeControl(control, this.textBlockCategory);
                    ctrl.on(ControlEventTypes.Resolve, (e) => this.resolve(<string>e.data, {}));
                    this.toolbarControls[ctrl.getControlId()] = ctrl;
                    let templ = ctrl.getTemplate(DisplayModes.Edit);
                    if (templ != null) {
                        if (column.ColumnTypeID == ColumnTypes.Submitbutton) {
                            submitButtons.push({ template: templ.template });
                        } else {
                            buttons.push({ template: templ.template });
                        }
                    }
                }
            }
        }
        columns = columns.concat(submitButtons, buttons);

        if (this.gridDefinition.ReportEnabled) {
            columns.push({
                template: `<button class="k-button k-button-icon dms-control dms-control-button ${
                    CssClass.export
                } dms-tooltip" data-dms-tooltip="${this.textBlockService.get(
                    "excelExport",
                    this.textBlockCategory,
                    true
                )}"><span class="k-icon k-i-excel color-export-icon" style="float: left"></span></button>`,
            });
        }

        return columns.length == 0 ? null : columns;
    }

    /**
     * Initialize the dms-controls in grid toolbar.
     * @hidden
     */
    protected initializeToolbar() {
        let toolbar = this.jQueryElement.find(".k-grid-toolbar");
        if (this.toolbarControls && Object.keys(this.toolbarControls).length > 0) {
            this.toolbarEditContext = new EditContext(
                this.baseGridDefinition,
                this.comboboxService,
                this.commandService,
                this.textBlockService
            );
            this.toolbarEditContext.setDataRowFunction(() => {
                return {};
            });
            this.toolbarEditContext.initialize(EditModes.Edit);

            this.initializeControlsInContainer(toolbar, this.toolbarControls, this.toolbarEditContext);

            this.toolbarEditContext.load(this.textBlockCategory, toolbar);
        }
    }

    /**
     * Register specific events on this control.
     * @param control Control.
     * @hidden
     */
    protected registerControlEvents(control: IControl) {
        if (control.getControlType() == GridControlTypes.DatasetCommand) {
            control.on(ControlEventTypes.BeforeCommand, () => this.beforeCommand());
            control.on(ControlEventTypes.GetCommandOption, (e) => this.commandOption);
            control.on(ControlEventTypes.AfterCommand, () => this.afterCommand());
            control.on(ControlEventTypes.AfterCommandCancel, () => this.afterCommandCancel());
        }
    }

    /**
     * This method is called for each column when entering edit/add mode of a row/cell.
     * @param columnDefinition Column.
     * @param container HTML-container where the controls should be added.
     * @param options Editor options, also includes the current datarow (options.model).
     * @hidden
     */
    protected getEditor(columnDefinition: IGridColumnDefinition, container: HTMLElement, options: kendo.ui.GridColumnEditorOptions): void {
        let controlDefinitions = columnDefinition.Controls;

        let isAdd = options.model[DataConstants.ROW_INDEX_COLUMN_NAME] === -1;

        for (let ctlIndex = 0, ctlLen = controlDefinitions.length; ctlIndex < ctlLen; ctlIndex++) {
            let controlDefinition = controlDefinitions[ctlIndex];

            // when the add line is shown immediately, hide the cancel button of the add line.
            if (controlDefinition.ControlTypeID == GridControlTypes.Cancel && this.gridDefinition.ShowAddLineImmediately && isAdd) {
                continue;
            }
            if (
                ((!isAdd && !controlDefinition.OnEdit) || (isAdd && !controlDefinition.OnAdd)) &&
                (controlDefinition.ControlTypeID == GridControlTypes.Link ||
                    controlDefinition.ControlTypeID == GridControlTypes.ContextMenu ||
                    controlDefinition.ControlTypeID == GridControlTypes.Command)
            ) {
                continue;
            }

            let ctl = CoreContext.Instance.initializeControl(controlDefinition, this.textBlockCategory);
            ctl.on(ControlEventTypes.Resolve, (e) => this.onResolveScript(e.sender, <string>e.data));
            ctl.modifyForDataRow(<IDataRow>(<any>options.model));
            let editorTemplate = ctl.getTemplate(isAdd ? DisplayModes.Add : DisplayModes.Edit, this.getControlFieldName(controlDefinition));

            if (editorTemplate != null) {
                let element = $(editorTemplate.template);

                if (editorTemplate.modelExtension != null) {
                    $.extend(options.model, editorTemplate.modelExtension);
                }

                element.appendTo(container);

                // register control on editContext
                this.editContext.registerControl(ctl);

                ctl.setElement(element);

                // register buttons to grid functions.
                switch (ctl.getControlType()) {
                    // cancel button
                    case GridControlTypes.Cancel:
                        ctl.on(ControlEventTypes.Click, (e) => this.cancelChanges(e.sender, true));
                        break;

                    // update/save button
                    case GridControlTypes.Update:
                        ctl.on(ControlEventTypes.BeforeCommand, () => this.beforeCommand());
                        ctl.on(ControlEventTypes.AfterCommand, () => this.afterUpdate());
                        ctl.on(ControlEventTypes.AfterCommandCancel, () => this.afterCommandCancel());
                        break;

                    // add button
                    case GridControlTypes.Add:
                        ctl.on(ControlEventTypes.BeforeCommand, () => this.beforeCommand());
                        ctl.on(ControlEventTypes.AfterCommand, () => this.afterAdd());
                        ctl.on(ControlEventTypes.AfterCommandCancel, () => this.afterCommandCancel());
                        break;
                }
            }
        }
    }

    /**
     * Set the grid busy.
     *
     * Only the table row area is covered with the busy indicator.
     * @param isBusy Pass true to show, false to hide the busy indicator.
     * @since 4.0.0
     */
    public setBusy(isBusy: boolean) {
        (<any>this._kendoElement)._progress(isBusy);
    }

    /**
     * Occurs after a command was cancelled.
     * @hidden
     */
    protected afterCommandCancel() {
        this.setBusy(false);
    }

    private beforeCommand(): void {
        this.setBusy(true);
        this._saveScrollPos = true;
        this.saveScrollPosition();
    }

    private afterCommand(): void {
        this.reload();
    }

    private afterAdd(): void {
        this.reload();
        this.notifyIfEnabled(this.textBlockService.get("NotificationAddRowSuccess", TextBlockCategory.global), NotificationTypes.Success);
    }

    private afterUpdate(): void {
        this.reload();
        this.notifyIfEnabled(this.textBlockService.get("NotificationEditRowSuccess", TextBlockCategory.global), NotificationTypes.Success);
    }

    private afterDelete(sender: IControl) {
        this.reload();
        this.notifyIfEnabled(
            this.textBlockService.get("NotificationDeleteRowSuccess", TextBlockCategory.global),
            NotificationTypes.Success
        );
    }

    /**
     * Render row editable.
     * @param sender Control / Button which triggered the edit.
     */
    private edit(sender: IControl) {
        this.cancelChanges(sender, false);

        let data = this.getDataRow(sender);

        this.editContext.initialize(EditModes.Edit);
        this._saveScrollPos = true;
        this._kendoElement.editRow(<any>data);
    }

    /**
     * Triggered when the current grid row is changed.
     * @param e EventArgs.
     * @hidden
     */
    // protected onChange(e: kendo.ui.GridChangeEvent) {
    // }

    /**
     * Cancel changes for the current row.
     * @param sender Control which triggered the cancel.
     * @param handleAddButton True if the add line has to be restored.
     * @hidden
     */
    protected cancelChanges(sender: IControl, handleAddButton: boolean): void {
        // get uid of current edited row
        let uid = this.currentEditUid;

        this._kendoElement.cancelRow();
        this.disposeEditContext();

        // find the row (=non-editable row) after cancel
        let row = this._kendoElement.content.find("tr[data-uid='" + uid + "']");
        // handle databound for the row
        this.handleDataBound(row, true);

        if (this._kendoElement.lockedContent) {
            row = this._kendoElement.lockedContent.find("tr[data-uid='" + uid + "']");
            // handle databound for the row
            this.handleDataBound(row);
        }

        if (handleAddButton) {
            // render the fake add line as editable
            if (this.gridDefinition.ShowAddLineImmediately && this.gridDefinition.AddAllowed) {
                this.onAddButton();
            }
        }
    }

    /**
     * Reload the data of the grid.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // Reload the first grid on the page.
     * @@grd:GridName@@.reload();
     * ```
     */
    public reload(): Promise<void> {
        this.disposeEditContext();
        this.resetInsertRowModel();
        this.show();
        (<any>this._kendoElement)._filterable(); // recreate filter menus to reload data when reopening the menus
        return Promise.resolve(this._kendoElement.dataSource.read({ reloadFromDataSource: true }));
    }

    /**
     * Get the kendo grid control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/grid|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.Grid {
        return this._kendoElement;
    }

    /**
     * Set the height of the grid.
     * @param height Height in pixel.
     * @since 4.0.0
     */
    public setHeight(height: string | number): void {
        super.setHeight(height);
        if (this._kendoElement) {
            this._kendoElement.wrapper.height(height);
            this._kendoElement.resize(true);
        }
    }

    /**
     * Clears the data of the grid.
     * @since 4.0.0
     */
    public clear(): void {
        if (this._kendoElement) {
            this._kendoElement.dataSource.data([]);
        }
    }

    /**
     * Clears all column filter values.
     * @since 4.2.0
     */
    public clearFilter(): void {
        if (this._kendoElement) {
            this._kendoElement.dataSource.filter({});
        }
    }

    /**
     * Find control in EditContext. Used in $dms-Selector
     * @param controlId ControlId to find.
     * @hidden
     */
    public findControl(control: number | HTMLElement): IControl {
        let ctrl: IControl;
        if (this.toolbarEditContext) {
            ctrl = this.toolbarEditContext.findControl(control);
        }
        if (!ctrl) {
            ctrl = this.editContext.findControl(control);
        }
        return ctrl;
    }

    /**
     * Get the current state of the grid as user grid config.
     * @hidden
     */
    public createUserGridConfig(): string {
        let options = this._kendoElement.getOptions();
        let config: IGridUserConfig = {
            Columns: [],
            PageSize: this._kendoElement.dataSource.pageSize(),
            SortDefinition: this._kendoElement.dataSource.sort(),
            SortRequiredFields: this.sortRequiredFields,
            GroupDefinition: this._kendoElement.dataSource.group(),
        };

        for (let i = 0, len = options.columns.length; i < len; i++) {
            let column = options.columns[i];
            if (!column.field) {
                continue;
            }
            config.Columns.push({
                IsHidden: column.hidden || false,
                ColumnName: column.field,
                Width: column.width && Number(column.width),
            });
        }
        return JSON.stringify(config);
    }

    /**
     * Apply the given user grid config to the grid.
     * @param config Selected configuration.
     * @hidden
     */
    public async applyUserGridConfig(config: IUserGridConfigDefinition): Promise<void> {
        await super.applyUserGridConfig(config);
        this.clear();
        this.dispose();
        this.jQueryElement.empty();
        this._kendoElement = null;
        await this.setElementInternal(this.jQueryElement);
    }

    /**
     * Dispose the grid and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
        this.disposeEditContext();

        if (this.toolbarEditContext) {
            this.toolbarEditContext.dispose();
        }
        if (this.jQueryElement) {
            this.jQueryElement.find(".dms-helptext").off();
        }
    }
}
